/**
 * Centralized translations for the Vital app.
 * Phase 1 scope: navigation, page titles, primary buttons, tabs, table headers,
 * modal titles, empty states, login screens — across landing + 4 dashboards.
 */

export const SUPPORTED_LOCALES = [
  { code: 'pt-BR', label: 'Português', short: 'PT' },
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'es', label: 'Español', short: 'ES' },
];

export const DEFAULT_LOCALE = 'pt-BR';

const translations = {
  'pt-BR': {
    // ─── Common ──────────────────────────────────────────────
    common: {
      save: 'Salvar', cancel: 'Cancelar', edit: 'Editar', delete: 'Excluir',
      back: 'Voltar', confirm: 'Confirmar', close: 'Fechar', search: 'Buscar',
      filter: 'Filtrar', loading: 'Carregando...', noData: 'Nenhum dado encontrado',
      yes: 'Sim', no: 'Não', actions: 'Ações', status: 'Status', name: 'Nome',
      email: 'E-mail', phone: 'Telefone', date: 'Data', total: 'Total',
      add: 'Adicionar', create: 'Criar', update: 'Atualizar', view: 'Visualizar',
      logout: 'Sair', settings: 'Configurações', refresh: 'Atualizar',
      submit: 'Enviar', next: 'Próximo', previous: 'Anterior', send: 'Enviar',
      saving: 'Salvando...', deleting: 'Excluindo...', loadMore: 'Carregar mais',
      active: 'Ativo', inactive: 'Inativo', pending: 'Pendente', approved: 'Aprovado',
      rejected: 'Rejeitado', success: 'Sucesso', error: 'Erro', warning: 'Atenção',
      required: 'Obrigatório', optional: 'Opcional', all: 'Todos',
      logoutSuccess: 'Sessão encerrada com sucesso',
      alerts: 'Alertas',
    },
    nav: {
      admin: 'Admin', student: 'Sou Aluno', login: 'Entrar', register: 'Criar Conta',
      menu: 'Menu',
    },
    hero: {
      titleA: 'Saúde Corporativa', titleB: 'Inteligente',
      subtitle: 'Gerencie e monitore a rotina de colaboradores em academias parceiras. Métricas objetivas, avaliações físicas e insights de IA para seu RH.',
      ctaCompany: 'VITAL PARA SUA EMPRESA',
      ctaGym: 'VITAL PARA SUA ACADEMIA',
      ctaYou: 'VITAL PARA VOCÊ',
      ctaStart: 'Começar Agora', ctaHaveAccount: 'Já tenho conta',
    },
    cta: {
      ready: 'Pronto para começar?',
      readySubtitle: 'Cuide de quem faz seu negócio crescer.',
      ctaFree: 'Criar Conta Grátis',
    },
    landing: {
      knowSystem: 'Conheça Nosso Sistema',
      selectProfile: 'Selecione o perfil que mais combina com você',
      btnCompany: 'VITAL PARA SUA EMPRESA',
      btnCompanyHint: 'Invista na sua equipe',
      btnGym: 'VITAL PARA SUA ACADEMIA',
      btnGymHint: 'Receita previsível',
      btnYou: 'VITAL PARA VOCÊ',
      btnYouHint: 'Bem-vindo à Vital',
      // Empresa section
      companyTitleA: 'Invista na sua equipe, é', companyTitleB: 'para todos!',
      companyTitleHint: 'Quanto mais colaboradores, menor o investimento por pessoa',
      tableRange: 'Faixa', tablePrice: 'Valor/Colab.', tableExample: 'Exemplo de Custo',
      tableUpTo: 'Até', tableCollabs: 'colaboradores', tableAbove100: 'Acima de 100',
      tableOnRequest: 'Sob consulta', tableMonth: 'mês', tableCustomAdmin: 'Valor editável pelo admin',
      tableEachCollab: 'colab. =',
      contactCta: 'Chame nossa equipe para uma cotação personalizada',
      // Academia section
      gymP1A: 'Na',
      gymP1B: ', não trabalhamos com simples distribuição de check-ins. Nós construímos',
      gymP1C: 'relacionamentos reais',
      gymP1D: ', conectando sua academia a alunos ativos e recorrentes, com previsibilidade financeira e gestão inteligente.',
      gymP2: 'Nosso modelo foi pensado para',
      gymP2B: 'valorizar o seu negócio',
      gymP2C: 'e garantir receita de forma consistente. Veja como você ganha:',
      gymCard1Title: '1. Receita fixa e previsível',
      gymCard1Body1: 'Ao ser escolhida como',
      gymCard1Body2: 'academia vínculo',
      gymCard1Body3: ', você recebe',
      gymCard1Body4: 'do valor do plano do aluno, independentemente da frequência de check-ins.',
      gymCard1Italic: 'Ou seja: o aluno escolheu sua academia, você já começa a lucrar.',
      gymCard2Title: '2. Receita variável e escalável',
      gymCard2Body1: 'Os outros',
      gymCard2Body2: 'do plano são distribuídos através de check-ins extras realizados por alunos em academias fora do vínculo.',
      gymCard2Italic: 'Sua academia também pode lucrar com alunos de toda a rede, aumentando seu faturamento conforme o uso do sistema.',
      gymQuestion: 'A pergunta é simples:',
      gymQuestionBody: 'Você prefere continuar dependendo do fluxo incerto do dia a dia... ou fazer parte de um sistema que transforma sua academia em uma fonte de receita',
      gymQuestionHighlight: 'previsível e escalável',
      gymInvite: 'Vamos conversar. A VITAL não traz apenas alunos — nós entregamos',
      gymInviteHighlight: 'crescimento estruturado',
      gymInviteEnd: 'para o seu negócio.',
      // Você section
      youWelcome: 'Bem-vindo à',
      youSubtitle: 'Você pode contratar direto em uma academia parceira ou, se sua empresa é conveniada, peça seu código ao RH.',
      youSubtitleHighlight: 'Ativação simples e acesso imediato.',
      youShow: 'Agora deixa eu te mostrar o que realmente importa: qual plano faz mais sentido pra você?',
      planExtra: 'Diferencial: +',
      planExtraSuffix: 'check-ins extras',
      dependent: 'Você pode também aderir um dependente e desfrutar dos benefícios com alguém especial!',
      notLocked: 'Você não está preso a uma única academia.',
      trainBase: 'Treina na sua base',
      explore: 'Explora outras academias quando quiser',
      freedom: 'Tem liberdade sem pagar várias mensalidades',
      priceNote: '*Os valores podem ser alterados para usuários cadastrados por empresas conveniadas',
      plans: {
        fit: { features: ['Ideal pra quem quer praticidade no dia a dia', 'Academias com atendimento próximo e eficiente', 'Treino rápido, sem complicação'] },
        plus: { features: ['Mais opções de academias', 'Melhor estrutura e mais horários disponíveis', 'Mais liberdade na sua rotina'] },
        premium: { features: ['Academias com padrão mais elevado', 'Equipamentos modernos', 'Ambiente mais focado em resultado'] },
        gold: { features: ['Estrutura diferenciada', 'Mais conforto e organização', 'Experiência de treino superior'] },
        platinum: { features: ['Alto padrão de academias', 'Mais opções disponíveis', 'Experiência premium'] },
        action: { features: ['Máxima liberdade de treino', 'Acesso às melhores academias', 'Foco total em performance'] },
      },
      // Features section
      featuresTitle: 'Recursos Principais',
      featuresSubtitle: 'Tudo que você precisa para gerenciar a saúde dos seus colaboradores',
      feat1Title: 'Dashboard Inteligente', feat1Desc: 'Métricas de saúde corporativa em tempo real com IA',
      feat2Title: 'Avaliações Físicas', feat2Desc: 'Monitoramento trimestral de IMC, gordura e massa muscular',
      feat3Title: 'Evolução Contínua', feat3Desc: 'Acompanhe o progresso dos colaboradores ao longo do tempo',
      feat4Title: 'Dados Protegidos', feat4Desc: 'Métricas consolidadas sem exposição de dados sensíveis',
      // Profiles section
      profilesTitle: 'Três Perfis, Uma Plataforma',
      profileHR: 'RH da Empresa',
      profileHRItems: ['Dashboard de saúde corporativa', 'Relatórios de frequência', 'Métricas de evolução física', 'Exportação PDF e Excel', 'Alertas de baixa frequência'],
      profileGym: 'Academia Parceira',
      profileGymItems: ['Cadastro de alunos corporativos', 'Check-in QR Code e geolocalização', 'Registro de avaliações físicas', 'Dashboard de engajamento', 'Controle por empresa'],
      profileStudent: 'Aluno Colaborador',
      profileStudentItems: ['Check-in diário rápido', 'Histórico de frequência', 'Resultados de avaliações', 'Gráficos de evolução', 'Alertas de avaliação'],
      // Stats
      statSatisfaction: 'Satisfação', statCompanies: 'Empresas',
      statCollabs: 'Colaboradores', statAbsenteeism: 'Redução Absenteísmo',
    },
    footer: { rights: '2025 Vital Performance. Todos os direitos reservados.' },
    login: {
      title: 'Entrar', subtitle: 'Acesse sua conta de Empresa ou Academia',
      email: 'E-mail', password: 'Senha', submit: 'Entrar',
      forgotPassword: 'Esqueci minha senha',
      noAccount: 'Não tem conta?', createOne: 'Criar conta',
      googleLogin: 'Continuar com Google',
    },
    student: {
      title: 'Entrar como Aluno', subtitle: 'Use seu código de acesso recebido por e-mail',
      codeLabel: 'Código de acesso', submit: 'Entrar',
    },
    video: {
      loading: 'Carregando vídeo...', enterSite: 'Acessar o site',
      watch: 'Assistir vídeo', closeAndEnter: 'Fechar vídeo e acessar o site',
      muteOn: 'Silenciar', muteOff: 'Ativar som',
    },
    language: { change: 'Idioma' },

    // ─── Admin Dashboard ─────────────────────────────────────
    adminNav: {
      panel: 'Painel', requests: 'Cadastros', companies: 'Empresas',
      gyms: 'Academias', students: 'Alunos', checkins: 'Check-ins',
      payments: 'Pagamentos', notifications: 'Avisos',
      coparticipations: 'Coparticipação', financial: 'Financeiro',
      content: 'Conteúdo', security: 'Segurança', auditLog: 'Log',
      performance: 'Performance',
    },
    admin: {
      welcome: 'Bem-vindo, Administrador',
      overviewTitle: 'Visão Geral',
      manageCompanies: 'Gerenciar Empresas', manageGyms: 'Gerenciar Academias',
      manageStudents: 'Gerenciar Alunos', revenue: 'Receita',
      empresasLabel: 'Empresas', academiasLabel: 'Academias', alunosLabel: 'Alunos',
      receitaTotal: 'Receita Total', visitas24h: 'Visitas (24h)',
      ultimosPagamentos: 'Últimos Pagamentos',
      cadastrosPendentes: 'Cadastros Pendentes',
      noPending: 'Nenhum cadastro pendente',
      approve: 'Aprovar', reject: 'Rejeitar',
      content: {
        title: 'Conteúdo do Site',
        subtitle: 'Gerencie o vídeo de apresentação exibido na tela inicial',
        videoCard: 'Vídeo de Apresentação',
        showOnLanding: 'Exibir vídeo na landing page',
        showOnLandingHint: 'Quando ativado, o modal aparece para todos os visitantes',
        tabUpload: 'Enviar arquivo', tabUrl: 'Colar URL',
        dropzone: 'Clique ou arraste o vídeo aqui',
        dropzoneHint: 'Formatos: .mp4, .mov, .webm, .m4v · Máximo: 100MB',
        storageInfo: 'O vídeo é enviado direto para o storage seguro do Emergent — sem necessidade de hospedagem externa.',
        urlLabel: 'URL do vídeo',
        urlHint: 'Cole o link direto para um arquivo .mp4 hospedado em CDN público.',
        saveChanges: 'Salvar alterações', openCurrent: 'Abrir vídeo atual',
        preview: 'Pré-visualização',
        activeMsg: '✅ Vídeo ativo — visível para todos os visitantes da landing page.',
        inactiveMsg: '⏸️ Vídeo desativado — não aparecerá para visitantes até ser reativado.',
        uploading: 'Enviando vídeo...',
      },
    },

    // ─── RH (Empresa) Dashboard ──────────────────────────────
    rhNav: {
      dashboard: 'Dashboard', ranking: 'Ranking', students: 'Colaboradores',
      messages: 'Mensagens', profile: 'Perfil', integrations: 'Integrações',
      contact: 'Contato',
    },
    rh: {
      roleLabel: 'RH',
      overviewTitle: 'Painel de Saúde Corporativa',
      activeCollaborators: 'Colaboradores ativos',
      checkinsMonth: 'Check-ins no mês',
      avgAttendance: 'Frequência média',
      newRegistrations: 'Novos cadastros',
      addCollaborator: 'Adicionar Colaborador',
      importCSV: 'Importar CSV', exportCSV: 'Exportar CSV',
      noCollaborators: 'Nenhum colaborador cadastrado',
      noRanking: 'Sem dados de ranking ainda',
      noMessages: 'Nenhuma mensagem enviada',
      companyProfile: 'Perfil da Empresa',
      sector: 'Setor', cnpj: 'CNPJ',
    },

    // ─── Gym Dashboard ───────────────────────────────────────
    gymNav: {
      dashboard: 'Dashboard', students: 'Alunos', checkins: 'Check-ins',
      history: 'Histórico', evaluations: 'Avaliações', profile: 'Perfil',
      financial: 'Financeiro', catraca: 'Catraca', contact: 'Contato',
    },
    gym: {
      roleLabel: 'Academia',
      overviewTitle: 'Painel da Academia',
      activeStudents: 'Alunos ativos',
      todayCheckins: 'Check-ins hoje',
      monthRevenue: 'Receita no mês',
      pendingPayments: 'Pagamentos pendentes',
      students: { title: 'Alunos', noStudents: 'Nenhum aluno encontrado' },
      checkins: { title: 'Check-ins', noCheckins: 'Sem check-ins hoje' },
      history: { title: 'Histórico de Check-ins' },
      evaluations: { title: 'Avaliações Físicas', newEvaluation: 'Nova Avaliação' },
      profile: { title: 'Perfil da Academia' },
      financial: { title: 'Painel Financeiro' },
      catraca: { title: 'Integração de Catraca' },
      contact: { title: 'Suporte e Contato' },
    },

    // ─── Student Dashboard ───────────────────────────────────
    studentNav: {
      checkin: 'Check-in', history: 'Histórico', evolution: 'Evolução',
      coparticipation: 'Coparticipação', profile: 'Perfil', contact: 'Contato',
    },
    studentDash: {
      roleLabel: 'Aluno',
      checkinTitle: 'Fazer Check-in',
      currentPlan: 'Plano atual',
      nextEvaluation: 'Próxima avaliação',
      historyTitle: 'Histórico de Check-ins',
      evolutionTitle: 'Sua Evolução',
      coparticipationTitle: 'Coparticipação',
      profileTitle: 'Meu Perfil',
      contactTitle: 'Suporte e Contato',
      noCheckins: 'Você ainda não fez nenhum check-in',
      noEvaluations: 'Nenhuma avaliação registrada',
    },

    // ─── Reset Password ──────────────────────────────────────
    reset: {
      title: 'Recuperar Senha',
      subtitle: 'Informe seu e-mail para receber instruções',
      emailLabel: 'E-mail', send: 'Enviar Link', sent: 'E-mail enviado!',
      backToLogin: 'Voltar ao login',
    },
  },

  en: {
    common: {
      save: 'Save', cancel: 'Cancel', edit: 'Edit', delete: 'Delete',
      back: 'Back', confirm: 'Confirm', close: 'Close', search: 'Search',
      filter: 'Filter', loading: 'Loading...', noData: 'No data found',
      yes: 'Yes', no: 'No', actions: 'Actions', status: 'Status', name: 'Name',
      email: 'Email', phone: 'Phone', date: 'Date', total: 'Total',
      add: 'Add', create: 'Create', update: 'Update', view: 'View',
      logout: 'Log out', settings: 'Settings', refresh: 'Refresh',
      submit: 'Submit', next: 'Next', previous: 'Previous', send: 'Send',
      saving: 'Saving...', deleting: 'Deleting...', loadMore: 'Load more',
      active: 'Active', inactive: 'Inactive', pending: 'Pending', approved: 'Approved',
      rejected: 'Rejected', success: 'Success', error: 'Error', warning: 'Warning',
      required: 'Required', optional: 'Optional', all: 'All',
      logoutSuccess: 'Session ended successfully',
      alerts: 'Alerts',
    },
    nav: {
      admin: 'Admin', student: 'I am a Member', login: 'Log in', register: 'Sign up',
      menu: 'Menu',
    },
    hero: {
      titleA: 'Smart Corporate', titleB: 'Wellness',
      subtitle: 'Manage and track employees across partner gyms. Objective metrics, physical assessments and AI insights for your HR team.',
      ctaCompany: 'VITAL FOR YOUR COMPANY',
      ctaGym: 'VITAL FOR YOUR GYM',
      ctaYou: 'VITAL FOR YOU',
      ctaStart: 'Get Started', ctaHaveAccount: 'I already have an account',
    },
    cta: {
      ready: 'Ready to start?',
      readySubtitle: 'Take care of the people who grow your business.',
      ctaFree: 'Create Free Account',
    },
    landing: {
      knowSystem: 'Discover Our System',
      selectProfile: 'Select the profile that best suits you',
      btnCompany: 'VITAL FOR YOUR COMPANY',
      btnCompanyHint: 'Invest in your team',
      btnGym: 'VITAL FOR YOUR GYM',
      btnGymHint: 'Predictable revenue',
      btnYou: 'VITAL FOR YOU',
      btnYouHint: 'Welcome to Vital',
      companyTitleA: 'Invest in your team, it is', companyTitleB: 'for everyone!',
      companyTitleHint: 'The more employees, the lower the per-person investment',
      tableRange: 'Tier', tablePrice: 'Per employee', tableExample: 'Sample cost',
      tableUpTo: 'Up to', tableCollabs: 'employees', tableAbove100: 'Above 100',
      tableOnRequest: 'On request', tableMonth: 'month', tableCustomAdmin: 'Value editable by admin',
      tableEachCollab: 'emp. =',
      contactCta: 'Contact our team for a personalized quote',
      gymP1A: 'At',
      gymP1B: ", we don't just distribute check-ins. We build",
      gymP1C: 'real relationships',
      gymP1D: ', connecting your gym to active, recurring members with financial predictability and smart management.',
      gymP2: 'Our model was designed to',
      gymP2B: 'value your business',
      gymP2C: 'and guarantee consistent revenue. Here is how you win:',
      gymCard1Title: '1. Fixed and predictable revenue',
      gymCard1Body1: 'When chosen as the',
      gymCard1Body2: 'home gym',
      gymCard1Body3: ', you receive',
      gymCard1Body4: "of the member's plan, regardless of check-in frequency.",
      gymCard1Italic: 'In short: when the member picks your gym, you start earning.',
      gymCard2Title: '2. Variable and scalable revenue',
      gymCard2Body1: 'The remaining',
      gymCard2Body2: 'of the plan is distributed through extra check-ins made at gyms outside the home gym.',
      gymCard2Italic: 'Your gym can also earn from members across the network, growing revenue as system usage grows.',
      gymQuestion: 'The question is simple:',
      gymQuestionBody: 'Do you prefer to keep depending on the uncertain daily flow... or be part of a system that turns your gym into a',
      gymQuestionHighlight: 'predictable and scalable',
      gymInvite: "Let's talk. VITAL not only brings members — we deliver",
      gymInviteHighlight: 'structured growth',
      gymInviteEnd: 'for your business.',
      youWelcome: 'Welcome to',
      youSubtitle: 'You can sign up directly with a partner gym or, if your company is enrolled, ask your HR for your code.',
      youSubtitleHighlight: 'Simple activation and instant access.',
      youShow: 'Now let me show you what really matters: which plan makes more sense for you?',
      planExtra: 'Bonus: +',
      planExtraSuffix: 'extra check-ins',
      dependent: 'You can also add a dependent and enjoy the benefits with someone special!',
      notLocked: 'You are not tied to a single gym.',
      trainBase: 'Train at your base',
      explore: 'Explore other gyms whenever you want',
      freedom: 'Freedom without paying multiple memberships',
      priceNote: '*Prices may differ for users registered through partner companies',
      plans: {
        fit: { features: ['Ideal for those wanting day-to-day convenience', 'Gyms with nearby and efficient service', 'Quick training, no fuss'] },
        plus: { features: ['More gym options', 'Better facilities and more available hours', 'More flexibility in your routine'] },
        premium: { features: ['Higher-standard gyms', 'Modern equipment', 'Goal-focused environment'] },
        gold: { features: ['Premium facilities', 'More comfort and organization', 'Superior training experience'] },
        platinum: { features: ['High-end gyms', 'More options available', 'Premium experience'] },
        action: { features: ['Maximum training freedom', 'Access to the best gyms', 'Total performance focus'] },
      },
      featuresTitle: 'Main Features',
      featuresSubtitle: 'Everything you need to manage your employees wellness',
      feat1Title: 'Smart Dashboard', feat1Desc: 'Real-time corporate health metrics with AI',
      feat2Title: 'Physical Assessments', feat2Desc: 'Quarterly tracking of BMI, body fat and lean mass',
      feat3Title: 'Continuous Progress', feat3Desc: 'Track employee progress over time',
      feat4Title: 'Data Protected', feat4Desc: 'Consolidated metrics with no sensitive data exposure',
      profilesTitle: 'Three Profiles, One Platform',
      profileHR: 'Company HR',
      profileHRItems: ['Corporate health dashboard', 'Attendance reports', 'Physical progress metrics', 'PDF and Excel export', 'Low attendance alerts'],
      profileGym: 'Partner Gym',
      profileGymItems: ['Corporate member registration', 'QR Code & geolocation check-in', 'Physical assessment records', 'Engagement dashboard', 'Per-company control'],
      profileStudent: 'Employee Member',
      profileStudentItems: ['Fast daily check-in', 'Attendance history', 'Assessment results', 'Progress charts', 'Assessment alerts'],
      statSatisfaction: 'Satisfaction', statCompanies: 'Companies',
      statCollabs: 'Employees', statAbsenteeism: 'Absenteeism Reduction',
    },
    footer: { rights: '2025 Vital Performance. All rights reserved.' },
    login: {
      title: 'Log in', subtitle: 'Access your Company or Gym account',
      email: 'Email', password: 'Password', submit: 'Log in',
      forgotPassword: 'Forgot my password',
      noAccount: 'No account?', createOne: 'Create an account',
      googleLogin: 'Continue with Google',
    },
    student: {
      title: 'Member login', subtitle: 'Use the access code sent to your email',
      codeLabel: 'Access code', submit: 'Log in',
    },
    video: {
      loading: 'Loading video...', enterSite: 'Enter the site',
      watch: 'Watch video', closeAndEnter: 'Close video and enter the site',
      muteOn: 'Mute', muteOff: 'Unmute',
    },
    language: { change: 'Language' },

    adminNav: {
      panel: 'Dashboard', requests: 'Requests', companies: 'Companies',
      gyms: 'Gyms', students: 'Members', checkins: 'Check-ins',
      payments: 'Payments', notifications: 'Notices',
      coparticipations: 'Co-payment', financial: 'Financial',
      content: 'Content', security: 'Security', auditLog: 'Log',
      performance: 'Performance',
    },
    admin: {
      welcome: 'Welcome, Administrator',
      overviewTitle: 'Overview',
      manageCompanies: 'Manage Companies', manageGyms: 'Manage Gyms',
      manageStudents: 'Manage Members', revenue: 'Revenue',
      empresasLabel: 'Companies', academiasLabel: 'Gyms', alunosLabel: 'Members',
      receitaTotal: 'Total Revenue', visitas24h: 'Visits (24h)',
      ultimosPagamentos: 'Recent Payments',
      cadastrosPendentes: 'Pending Requests',
      noPending: 'No pending requests',
      approve: 'Approve', reject: 'Reject',
      content: {
        title: 'Site Content',
        subtitle: 'Manage the intro video displayed on the landing page',
        videoCard: 'Intro Video',
        showOnLanding: 'Show video on landing page',
        showOnLandingHint: 'When enabled, the modal appears for every visitor',
        tabUpload: 'Upload file', tabUrl: 'Paste URL',
        dropzone: 'Click or drag the video here',
        dropzoneHint: 'Formats: .mp4, .mov, .webm, .m4v · Max: 100MB',
        storageInfo: 'The video is uploaded directly to Emergent secure storage — no external hosting required.',
        urlLabel: 'Video URL',
        urlHint: 'Paste a direct link to an .mp4 hosted on a public CDN.',
        saveChanges: 'Save changes', openCurrent: 'Open current video',
        preview: 'Preview',
        activeMsg: '✅ Video active — visible to all landing page visitors.',
        inactiveMsg: '⏸️ Video disabled — will not appear until re-enabled.',
        uploading: 'Uploading video...',
      },
    },

    rhNav: {
      dashboard: 'Dashboard', ranking: 'Ranking', students: 'Employees',
      messages: 'Messages', profile: 'Profile', integrations: 'Integrations',
      contact: 'Contact',
    },
    rh: {
      roleLabel: 'HR',
      overviewTitle: 'Corporate Wellness Dashboard',
      activeCollaborators: 'Active employees',
      checkinsMonth: 'Check-ins this month',
      avgAttendance: 'Average attendance',
      newRegistrations: 'New registrations',
      addCollaborator: 'Add Employee',
      importCSV: 'Import CSV', exportCSV: 'Export CSV',
      noCollaborators: 'No employees registered',
      noRanking: 'No ranking data yet',
      noMessages: 'No messages sent',
      companyProfile: 'Company Profile',
      sector: 'Sector', cnpj: 'Tax ID',
    },

    gymNav: {
      dashboard: 'Dashboard', students: 'Members', checkins: 'Check-ins',
      history: 'History', evaluations: 'Assessments', profile: 'Profile',
      financial: 'Financial', catraca: 'Turnstile', contact: 'Contact',
    },
    gym: {
      roleLabel: 'Gym',
      overviewTitle: 'Gym Dashboard',
      activeStudents: 'Active members',
      todayCheckins: 'Check-ins today',
      monthRevenue: 'Revenue this month',
      pendingPayments: 'Pending payments',
      students: { title: 'Members', noStudents: 'No members found' },
      checkins: { title: 'Check-ins', noCheckins: 'No check-ins today' },
      history: { title: 'Check-in History' },
      evaluations: { title: 'Physical Assessments', newEvaluation: 'New Assessment' },
      profile: { title: 'Gym Profile' },
      financial: { title: 'Financial Dashboard' },
      catraca: { title: 'Turnstile Integration' },
      contact: { title: 'Support & Contact' },
    },

    studentNav: {
      checkin: 'Check-in', history: 'History', evolution: 'Progress',
      coparticipation: 'Co-payment', profile: 'Profile', contact: 'Contact',
    },
    studentDash: {
      roleLabel: 'Member',
      checkinTitle: 'Check In',
      currentPlan: 'Current plan',
      nextEvaluation: 'Next assessment',
      historyTitle: 'Check-in History',
      evolutionTitle: 'Your Progress',
      coparticipationTitle: 'Co-payment',
      profileTitle: 'My Profile',
      contactTitle: 'Support & Contact',
      noCheckins: 'You have not checked in yet',
      noEvaluations: 'No assessments recorded',
    },

    reset: {
      title: 'Reset Password',
      subtitle: 'Enter your email to receive instructions',
      emailLabel: 'Email', send: 'Send link', sent: 'Email sent!',
      backToLogin: 'Back to login',
    },
  },

  es: {
    common: {
      save: 'Guardar', cancel: 'Cancelar', edit: 'Editar', delete: 'Eliminar',
      back: 'Volver', confirm: 'Confirmar', close: 'Cerrar', search: 'Buscar',
      filter: 'Filtrar', loading: 'Cargando...', noData: 'Sin datos',
      yes: 'Sí', no: 'No', actions: 'Acciones', status: 'Estado', name: 'Nombre',
      email: 'Correo', phone: 'Teléfono', date: 'Fecha', total: 'Total',
      add: 'Agregar', create: 'Crear', update: 'Actualizar', view: 'Ver',
      logout: 'Salir', settings: 'Configuración', refresh: 'Actualizar',
      submit: 'Enviar', next: 'Siguiente', previous: 'Anterior', send: 'Enviar',
      saving: 'Guardando...', deleting: 'Eliminando...', loadMore: 'Cargar más',
      active: 'Activo', inactive: 'Inactivo', pending: 'Pendiente', approved: 'Aprobado',
      rejected: 'Rechazado', success: 'Éxito', error: 'Error', warning: 'Atención',
      required: 'Obligatorio', optional: 'Opcional', all: 'Todos',
      logoutSuccess: 'Sesión cerrada con éxito',
      alerts: 'Alertas',
    },
    nav: {
      admin: 'Admin', student: 'Soy Alumno', login: 'Ingresar', register: 'Crear Cuenta',
      menu: 'Menú',
    },
    hero: {
      titleA: 'Salud Corporativa', titleB: 'Inteligente',
      subtitle: 'Gestione y monitoree la rutina de los colaboradores en gimnasios asociados. Métricas objetivas, evaluaciones físicas e insights de IA para su RR.HH.',
      ctaCompany: 'VITAL PARA TU EMPRESA',
      ctaGym: 'VITAL PARA TU GIMNASIO',
      ctaYou: 'VITAL PARA TI',
      ctaStart: 'Comenzar Ahora', ctaHaveAccount: 'Ya tengo cuenta',
    },
    cta: {
      ready: '¿Listo para empezar?',
      readySubtitle: 'Cuide de quienes hacen crecer su negocio.',
      ctaFree: 'Crear Cuenta Gratis',
    },
    landing: {
      knowSystem: 'Conozca Nuestro Sistema',
      selectProfile: 'Seleccione el perfil que más le conviene',
      btnCompany: 'VITAL PARA TU EMPRESA',
      btnCompanyHint: 'Invierte en tu equipo',
      btnGym: 'VITAL PARA TU GIMNASIO',
      btnGymHint: 'Ingresos previsibles',
      btnYou: 'VITAL PARA TI',
      btnYouHint: 'Bienvenido a Vital',
      companyTitleA: 'Invierte en tu equipo, es', companyTitleB: 'para todos!',
      companyTitleHint: 'Cuantos más colaboradores, menor la inversión por persona',
      tableRange: 'Rango', tablePrice: 'Por colaborador', tableExample: 'Costo ejemplo',
      tableUpTo: 'Hasta', tableCollabs: 'colaboradores', tableAbove100: 'Más de 100',
      tableOnRequest: 'A consultar', tableMonth: 'mes', tableCustomAdmin: 'Valor editable por el admin',
      tableEachCollab: 'colab. =',
      contactCta: 'Contacte a nuestro equipo para una cotización personalizada',
      gymP1A: 'En',
      gymP1B: ', no trabajamos con simple distribución de check-ins. Construimos',
      gymP1C: 'relaciones reales',
      gymP1D: ', conectando tu gimnasio con alumnos activos y recurrentes, con previsibilidad financiera y gestión inteligente.',
      gymP2: 'Nuestro modelo fue pensado para',
      gymP2B: 'valorar tu negocio',
      gymP2C: 'y garantizar ingresos consistentes. Mira cómo ganas:',
      gymCard1Title: '1. Ingresos fijos y previsibles',
      gymCard1Body1: 'Al ser elegido como',
      gymCard1Body2: 'gimnasio vínculo',
      gymCard1Body3: ', recibes',
      gymCard1Body4: 'del plan del alumno, sin importar la frecuencia de check-ins.',
      gymCard1Italic: 'En resumen: si el alumno eligió tu gimnasio, ya comienzas a ganar.',
      gymCard2Title: '2. Ingresos variables y escalables',
      gymCard2Body1: 'El',
      gymCard2Body2: 'restante del plan se distribuye mediante check-ins extras realizados en gimnasios fuera del vínculo.',
      gymCard2Italic: 'Tu gimnasio también puede ganar con alumnos de toda la red, aumentando ingresos conforme crece el uso del sistema.',
      gymQuestion: 'La pregunta es simple:',
      gymQuestionBody: '¿Prefieres seguir dependiendo del flujo incierto del día a día... o formar parte de un sistema que convierte tu gimnasio en una fuente de ingresos',
      gymQuestionHighlight: 'previsible y escalable',
      gymInvite: 'Hablemos. VITAL no solo trae alumnos — entregamos',
      gymInviteHighlight: 'crecimiento estructurado',
      gymInviteEnd: 'para tu negocio.',
      youWelcome: 'Bienvenido a',
      youSubtitle: 'Puedes contratar directamente en un gimnasio asociado o, si tu empresa está vinculada, pide tu código al RR.HH.',
      youSubtitleHighlight: 'Activación simple y acceso inmediato.',
      youShow: 'Ahora déjame mostrarte lo que realmente importa: ¿qué plan tiene más sentido para ti?',
      planExtra: 'Extra: +',
      planExtraSuffix: 'check-ins extras',
      dependent: '¡También puedes agregar un dependiente y disfrutar los beneficios con alguien especial!',
      notLocked: 'No estás atado a un solo gimnasio.',
      trainBase: 'Entrena en tu base',
      explore: 'Explora otros gimnasios cuando quieras',
      freedom: 'Libertad sin pagar varias mensualidades',
      priceNote: '*Los valores pueden cambiar para usuarios registrados por empresas asociadas',
      plans: {
        fit: { features: ['Ideal para quien busca practicidad en el día a día', 'Gimnasios con atención cercana y eficiente', 'Entrenamiento rápido, sin complicaciones'] },
        plus: { features: ['Más opciones de gimnasios', 'Mejor estructura y más horarios disponibles', 'Más libertad en tu rutina'] },
        premium: { features: ['Gimnasios de mayor categoría', 'Equipamiento moderno', 'Ambiente enfocado en resultados'] },
        gold: { features: ['Estructura diferenciada', 'Más comodidad y organización', 'Experiencia de entrenamiento superior'] },
        platinum: { features: ['Gimnasios de alto nivel', 'Más opciones disponibles', 'Experiencia premium'] },
        action: { features: ['Máxima libertad de entrenamiento', 'Acceso a los mejores gimnasios', 'Foco total en rendimiento'] },
      },
      featuresTitle: 'Características Principales',
      featuresSubtitle: 'Todo lo que necesitas para gestionar la salud de tus colaboradores',
      feat1Title: 'Dashboard Inteligente', feat1Desc: 'Métricas de salud corporativa en tiempo real con IA',
      feat2Title: 'Evaluaciones Físicas', feat2Desc: 'Monitoreo trimestral de IMC, grasa y masa muscular',
      feat3Title: 'Evolución Continua', feat3Desc: 'Sigue el progreso de los colaboradores a lo largo del tiempo',
      feat4Title: 'Datos Protegidos', feat4Desc: 'Métricas consolidadas sin exposición de datos sensibles',
      profilesTitle: 'Tres Perfiles, Una Plataforma',
      profileHR: 'RR.HH. de la Empresa',
      profileHRItems: ['Dashboard de salud corporativa', 'Reportes de asistencia', 'Métricas de evolución física', 'Exportación PDF y Excel', 'Alertas de baja frecuencia'],
      profileGym: 'Gimnasio Asociado',
      profileGymItems: ['Registro de alumnos corporativos', 'Check-in con QR y geolocalización', 'Registro de evaluaciones físicas', 'Dashboard de participación', 'Control por empresa'],
      profileStudent: 'Alumno Colaborador',
      profileStudentItems: ['Check-in diario rápido', 'Historial de frecuencia', 'Resultados de evaluaciones', 'Gráficos de evolución', 'Alertas de evaluación'],
      statSatisfaction: 'Satisfacción', statCompanies: 'Empresas',
      statCollabs: 'Colaboradores', statAbsenteeism: 'Reducción de Ausentismo',
    },
    footer: { rights: '2025 Vital Performance. Todos los derechos reservados.' },
    login: {
      title: 'Ingresar', subtitle: 'Acceda a su cuenta de Empresa o Gimnasio',
      email: 'Correo electrónico', password: 'Contraseña', submit: 'Ingresar',
      forgotPassword: 'Olvidé mi contraseña',
      noAccount: '¿No tienes cuenta?', createOne: 'Crear una cuenta',
      googleLogin: 'Continuar con Google',
    },
    student: {
      title: 'Ingresar como Alumno', subtitle: 'Use el código de acceso enviado por correo',
      codeLabel: 'Código de acceso', submit: 'Ingresar',
    },
    video: {
      loading: 'Cargando video...', enterSite: 'Acceder al sitio',
      watch: 'Ver video', closeAndEnter: 'Cerrar video y acceder al sitio',
      muteOn: 'Silenciar', muteOff: 'Activar sonido',
    },
    language: { change: 'Idioma' },

    adminNav: {
      panel: 'Panel', requests: 'Registros', companies: 'Empresas',
      gyms: 'Gimnasios', students: 'Alumnos', checkins: 'Check-ins',
      payments: 'Pagos', notifications: 'Avisos',
      coparticipations: 'Coparticipación', financial: 'Financiero',
      content: 'Contenido', security: 'Seguridad', auditLog: 'Log',
      performance: 'Rendimiento',
    },
    admin: {
      welcome: 'Bienvenido, Administrador',
      overviewTitle: 'Visión General',
      manageCompanies: 'Gestionar Empresas', manageGyms: 'Gestionar Gimnasios',
      manageStudents: 'Gestionar Alumnos', revenue: 'Ingresos',
      empresasLabel: 'Empresas', academiasLabel: 'Gimnasios', alunosLabel: 'Alumnos',
      receitaTotal: 'Ingreso Total', visitas24h: 'Visitas (24h)',
      ultimosPagamentos: 'Últimos Pagos',
      cadastrosPendentes: 'Registros Pendientes',
      noPending: 'Ningún registro pendiente',
      approve: 'Aprobar', reject: 'Rechazar',
      content: {
        title: 'Contenido del Sitio',
        subtitle: 'Gestione el video de presentación mostrado en la pantalla inicial',
        videoCard: 'Video de Presentación',
        showOnLanding: 'Mostrar video en la página principal',
        showOnLandingHint: 'Cuando está activado, el modal aparece para todos los visitantes',
        tabUpload: 'Subir archivo', tabUrl: 'Pegar URL',
        dropzone: 'Haz clic o arrastra el video aquí',
        dropzoneHint: 'Formatos: .mp4, .mov, .webm, .m4v · Máximo: 100MB',
        storageInfo: 'El video se sube directamente al almacenamiento seguro de Emergent — sin necesidad de hospedaje externo.',
        urlLabel: 'URL del video',
        urlHint: 'Pegue el enlace directo a un archivo .mp4 alojado en un CDN público.',
        saveChanges: 'Guardar cambios', openCurrent: 'Abrir video actual',
        preview: 'Vista previa',
        activeMsg: '✅ Video activo — visible para todos los visitantes.',
        inactiveMsg: '⏸️ Video desactivado — no aparecerá hasta ser reactivado.',
        uploading: 'Subiendo video...',
      },
    },

    rhNav: {
      dashboard: 'Dashboard', ranking: 'Ranking', students: 'Colaboradores',
      messages: 'Mensajes', profile: 'Perfil', integrations: 'Integraciones',
      contact: 'Contacto',
    },
    rh: {
      roleLabel: 'RR.HH.',
      overviewTitle: 'Panel de Salud Corporativa',
      activeCollaborators: 'Colaboradores activos',
      checkinsMonth: 'Check-ins del mes',
      avgAttendance: 'Frecuencia promedio',
      newRegistrations: 'Nuevos registros',
      addCollaborator: 'Agregar Colaborador',
      importCSV: 'Importar CSV', exportCSV: 'Exportar CSV',
      noCollaborators: 'No hay colaboradores registrados',
      noRanking: 'Sin datos de ranking aún',
      noMessages: 'Ningún mensaje enviado',
      companyProfile: 'Perfil de la Empresa',
      sector: 'Sector', cnpj: 'Identificación Fiscal',
    },

    gymNav: {
      dashboard: 'Dashboard', students: 'Alumnos', checkins: 'Check-ins',
      history: 'Historial', evaluations: 'Evaluaciones', profile: 'Perfil',
      financial: 'Financiero', catraca: 'Torniquete', contact: 'Contacto',
    },
    gym: {
      roleLabel: 'Gimnasio',
      overviewTitle: 'Panel del Gimnasio',
      activeStudents: 'Alumnos activos',
      todayCheckins: 'Check-ins hoy',
      monthRevenue: 'Ingresos del mes',
      pendingPayments: 'Pagos pendientes',
      students: { title: 'Alumnos', noStudents: 'No se encontraron alumnos' },
      checkins: { title: 'Check-ins', noCheckins: 'Sin check-ins hoy' },
      history: { title: 'Historial de Check-ins' },
      evaluations: { title: 'Evaluaciones Físicas', newEvaluation: 'Nueva Evaluación' },
      profile: { title: 'Perfil del Gimnasio' },
      financial: { title: 'Panel Financiero' },
      catraca: { title: 'Integración del Torniquete' },
      contact: { title: 'Soporte y Contacto' },
    },

    studentNav: {
      checkin: 'Check-in', history: 'Historial', evolution: 'Evolución',
      coparticipation: 'Coparticipación', profile: 'Perfil', contact: 'Contacto',
    },
    studentDash: {
      roleLabel: 'Alumno',
      checkinTitle: 'Hacer Check-in',
      currentPlan: 'Plan actual',
      nextEvaluation: 'Próxima evaluación',
      historyTitle: 'Historial de Check-ins',
      evolutionTitle: 'Tu Evolución',
      coparticipationTitle: 'Coparticipación',
      profileTitle: 'Mi Perfil',
      contactTitle: 'Soporte y Contacto',
      noCheckins: 'Aún no has hecho ningún check-in',
      noEvaluations: 'Ninguna evaluación registrada',
    },

    reset: {
      title: 'Recuperar Contraseña',
      subtitle: 'Ingrese su correo para recibir las instrucciones',
      emailLabel: 'Correo', send: 'Enviar enlace', sent: '¡Correo enviado!',
      backToLogin: 'Volver al login',
    },
  },
};

export default translations;
